import { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { paieAPI, employeesAPI } from '../../services/api';
import './PaieModuleFonctionnel.css';

/**
 * PaieModuleFonctionnel - Système complet de gestion de la paie
 * Gestion des bulletins de paie avec calculs automatiques
 * 
 * @param {boolean} readOnly - Mode lecture seule (désactive les actions d'édition)
 */

const PaieModuleFonctionnel = ({ readOnly = false }) => {
  const [bulletins, setBulletins] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  
  // Filtres
  const [filters, setFilters] = useState({
    mois: new Date().toISOString().slice(0, 7), // YYYY-MM format
    employee_id: '',
    statut: ''
  });

  // Formulaire
  const [formData, setFormData] = useState({
    employee_id: '',
    mois: new Date().toISOString().slice(0, 7),
    salaire_base: '',
    primes: '',
    heures_supplementaires: '',
    avantages_nature: '',
    autres_retenues: '',
    avances: '',
    acomptes: '',
    saisies_arrets: '',
    details_oppositions: '',
    notes: ''
  });

  // Calculs en temps réel
  const [calculs, setCalculs] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [bulletinsRes, employeesRes] = await Promise.all([
        paieAPI.bulletins.getAll(filters),
        employeesAPI.getAll()
      ]);
      
      setBulletins(bulletinsRes.bulletins || []);
      setEmployees(employeesRes.employees || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calcul en temps réel quand on modifie le formulaire
  const calculerPaie = useCallback(async () => {
    try {
      const data = {
        salaire_base: parseFloat(formData.salaire_base) || 0,
        primes: parseFloat(formData.primes) || 0,
        heures_supplementaires: parseFloat(formData.heures_supplementaires) || 0,
        avantages_nature: parseFloat(formData.avantages_nature) || 0,
        autres_retenues: parseFloat(formData.autres_retenues) || 0,
        avances: parseFloat(formData.avances) || 0,
        acomptes: parseFloat(formData.acomptes) || 0,
        saisies_arrets: parseFloat(formData.saisies_arrets) || 0,
      };
      
      const result = await paieAPI.bulletins.calculer(data);
      setCalculs(result.calculs);
    } catch (error) {
      console.error('Erreur calcul:', error);
    }
  }, [formData]);

  useEffect(() => {
    if (formData.salaire_base) {
      calculerPaie();
    }
  }, [formData.salaire_base, formData.primes, formData.heures_supplementaires, formData.avantages_nature, formData.autres_retenues, formData.avances, formData.acomptes, formData.saisies_arrets, calculerPaie]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      alert('Veuillez sélectionner un employé');
      return;
    }

    try {
      const data = {
        employee_id: parseInt(formData.employee_id),
        mois: formData.mois,
        salaire_base: parseInt(formData.salaire_base) || 0,
        primes: parseInt(formData.primes) || 0,
        heures_supplementaires: parseInt(formData.heures_supplementaires) || 0,
        avantages_nature: parseInt(formData.avantages_nature) || 0,
        autres_retenues: parseInt(formData.autres_retenues) || 0,
        avances: parseInt(formData.avances) || 0,
        acomptes: parseInt(formData.acomptes) || 0,
        saisies_arrets: parseInt(formData.saisies_arrets) || 0,
        details_oppositions: formData.details_oppositions,
        notes: formData.notes
      };

      if (selectedBulletin) {
        await paieAPI.bulletins.update(selectedBulletin.id, data);
        alert('Bulletin mis à jour avec succès');
      } else {
        await paieAPI.bulletins.create(data);
        alert('Bulletin créé avec succès');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert(error.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleGenererMasse = async () => {
    if (!window.confirm(`Générer les bulletins pour tous les employés du mois ${filters.mois} ?`)) {
      return;
    }

    try {
      const result = await paieAPI.bulletins.genererMasse({ mois: filters.mois });
      alert(`${result.stats.crees} bulletins créés, ${result.stats.ignores} ignorés (déjà existants)`);
      loadData();
    } catch (error) {
      console.error('Erreur génération masse:', error);
      alert(error.message || 'Erreur lors de la génération en masse');
    }
  };

  const handleValider = async (id) => {
    if (!window.confirm('Valider ce bulletin de paie ?')) return;
    
    try {
      await paieAPI.bulletins.valider(id);
      alert('Bulletin validé');
      loadData();
    } catch (error) {
      alert(error.message || 'Erreur lors de la validation');
    }
  };

  const handlePayer = async (id) => {
    const datePaiement = prompt('Date de paiement (YYYY-MM-DD):');
    const modePaiement = prompt('Mode de paiement (virement/especes/cheque/mobile):');
    const reference = prompt('Référence de paiement (optionnel):');
    
    if (!datePaiement || !modePaiement) {
      alert('Date et mode de paiement requis');
      return;
    }

    try {
      await paieAPI.bulletins.payer(id, {
        date_paiement: datePaiement,
        mode_paiement: modePaiement,
        reference_paiement: reference
      });
      alert('Bulletin marqué comme payé');
      loadData();
    } catch (error) {
      alert(error.message || 'Erreur lors du marquage');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bulletin ? (impossible si déjà payé)')) return;
    
    try {
      await paieAPI.bulletins.delete(id);
      alert('Bulletin supprimé');
      loadData();
    } catch (error) {
      alert(error.message || 'Erreur lors de la suppression');
    }
  };

  const openModal = (bulletin = null) => {
    if (bulletin) {
      setSelectedBulletin(bulletin);
      setFormData({
        employee_id: bulletin.employee_id,
        mois: bulletin.mois,
        salaire_base: bulletin.salaire_base,
        primes: bulletin.primes || '',
        heures_supplementaires: bulletin.heures_supplementaires || '',
        avantages_nature: bulletin.avantages_nature || '',
        autres_retenues: bulletin.autres_retenues || '',
        avances: bulletin.avances || '',
        acomptes: bulletin.acomptes || '',
        saisies_arrets: bulletin.saisies_arrets || '',
        details_oppositions: bulletin.details_oppositions || '',
        notes: bulletin.notes || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedBulletin(null);
    setFormData({
      employee_id: '',
      mois: new Date().toISOString().slice(0, 7),
      salaire_base: '',
      primes: '',
      heures_supplementaires: '',
      avantages_nature: '',
      autres_retenues: '',
      avances: '',
      acomptes: '',
      saisies_arrets: '',
      details_oppositions: '',
      notes: ''
    });
    setCalculs(null);
  };

  const fmtN = (num) => {
    const n = parseInt(num) || 0;
    return n.toLocaleString('fr-FR');
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { label: 'Brouillon', color: '#6c757d' },
      valide: { label: 'Validé', color: '#28a745' },
      paye: { label: 'Payé', color: '#007bff' }
    };
    const badge = badges[statut] || badges.brouillon;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: badge.color,
        color: 'white'
      }}>
        {badge.label}
      </span>
    );
  };

  const viewDetails = (bulletin) => {
    setSelectedBulletin(bulletin);
    setViewMode('print');
  };

  const printBulletin = (bulletin) => {
    setSelectedBulletin(bulletin);
    setViewMode('print');
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const downloadPdfBulletin = async (bulletin) => {
    try {
      setSelectedBulletin(bulletin);
      setViewMode('print');

      await new Promise((resolve) => setTimeout(resolve, 400));

      const element = document.getElementById('bulletin-pdf-export');
      if (!element) {
        alert('Aperçu introuvable pour l’export PDF');
        return;
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const nom = (bulletin.employee?.user?.name || 'bulletin').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      pdf.save(`bulletin-paie-${nom}-${bulletin.mois || 'fiche'}.pdf`);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert('Impossible de générer le PDF pour le moment');
    }
  };

  // Données entreprise fixes
  const ENTREPRISE = {
    nom: "FHC Groupe SARL",
    cnss: "BJ-CNSS-2022-4521",
    rccm: "RB/ABC/22 B 5806",
    adresse: "Abomey-Calavi, Bénin",
    telephone: "+229 XX XX XX XX",
    email: "contact@fhcgroupe.com"
  };

  const getMoisFormate = (moisStr) => {
    const moisNoms = {
      '01': 'Janvier', '02': 'Février', '03': 'Mars',
      '04': 'Avril', '05': 'Mai', '06': 'Juin',
      '07': 'Juillet', '08': 'Août', '09': 'Septembre',
      '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
    };
    const [annee, mois] = (moisStr || '').split('-');
    return `${moisNoms[mois] || 'Janvier'} ${annee || new Date().getFullYear()}`;
  };

  // Vue impression format FHC
  if (viewMode === 'print' && selectedBulletin) {
    const { employee } = selectedBulletin;
    const user = employee?.user || {};
    const totalRetenues = parseInt(selectedBulletin.cnss_salarie || 0) + 
                          parseInt(selectedBulletin.its || 0) + 
                          parseInt(selectedBulletin.autres_retenues || 0) +
                          parseInt(selectedBulletin.avances || 0) +
                          parseInt(selectedBulletin.acomptes || 0) +
                          parseInt(selectedBulletin.saisies_arrets || 0);

    return (
      <div className="fiche-paie-wrapper" id="bulletin-pdf-export">
        {/* Bouton retour en haut (non imprimable) */}
        <div className="no-print" style={{
          padding: '16px',
          backgroundColor: '#0a1628',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          marginBottom: '20px'
        }}>
          <button 
            onClick={() => { setViewMode('list'); setSelectedBulletin(null); }} 
            style={{
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Retour à la liste
          </button>
        </div>
        
        <div className="fiche-paie">
          {/* EN-TÊTE */}
          <div className="fp-header">
            <div className="fp-logo">
              <div className="fp-logo-main">{ENTREPRISE.nom}</div>
              <div className="fp-logo-sub">Groupe Management & Conseil</div>
            </div>
            <div className="fp-title">
              <div className="fp-title-main">BULLETIN DE PAIE</div>
              <div className="fp-title-period">
                Mois: {getMoisFormate(selectedBulletin.mois)}
              </div>
            </div>
          </div>
          
          {/* BANDE DORÉE */}
          <div className="fp-band"></div>
          
          {/* CORPS */}
          <div className="fp-body">
            {/* INFORMATIONS */}
            <div className="fp-info-grid">
              {/* EMPLOYÉ */}
              <div className="fp-info-block">
                <label>Employé(e)</label>
                <div className="fp-info-row">
                  <span>Nom complet</span>
                  <strong>{user.name || `${user.prenom || ''} ${user.nom || ''}`}</strong>
                </div>
                <div className="fp-info-row">
                  <span>Poste</span>
                  <strong>{employee?.poste || 'N/A'}</strong>
                </div>
                <div className="fp-info-row">
                  <span>Type contrat</span>
                  <strong>{employee?.contrat || 'CDI'}</strong>
                </div>
                <div className="fp-info-row">
                  <span>Date d'entrée</span>
                  <strong>{employee?.date_entree || 'N/A'}</strong>
                </div>
              </div>
              
              {/* EMPLOYEUR */}
              <div className="fp-info-block">
                <label>Employeur</label>
                <div className="fp-info-row">
                  <span>Raison sociale</span>
                  <strong>{ENTREPRISE.nom}</strong>
                </div>
                <div className="fp-info-row">
                  <span>N° CNSS</span>
                  <strong>{ENTREPRISE.cnss}</strong>
                </div>
                <div className="fp-info-row">
                  <span>RCCM</span>
                  <strong>{ENTREPRISE.rccm}</strong>
                </div>
                <div className="fp-info-row">
                  <span>Adresse</span>
                  <strong>{ENTREPRISE.adresse}</strong>
                </div>
              </div>
            </div>
            
            {/* TABLEAU DES ÉLÉMENTS */}
            <table className="fp-table">
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th>Taux</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {/* GAINS */}
                <tr className="section-header">
                  <td colSpan="3">ÉLÉMENTS DE RÉMUNÉRATION</td>
                </tr>
                <tr>
                  <td>Salaire de base</td>
                  <td>—</td>
                  <td>{fmtN(selectedBulletin.salaire_base)} F</td>
                </tr>
                {selectedBulletin.primes > 0 && (
                  <tr>
                    <td>Primes diverses</td>
                    <td>—</td>
                    <td>{fmtN(selectedBulletin.primes)} F</td>
                  </tr>
                )}
                {selectedBulletin.heures_supplementaires > 0 && (
                  <tr>
                    <td>Heures supplémentaires</td>
                    <td>—</td>
                    <td>{fmtN(selectedBulletin.heures_supplementaires)} F</td>
                  </tr>
                )}
                {selectedBulletin.avantages_nature > 0 && (
                  <tr>
                    <td>Avantages en nature</td>
                    <td>—</td>
                    <td>{fmtN(selectedBulletin.avantages_nature)} F</td>
                  </tr>
                )}
                <tr className="subtotal">
                  <td>TOTAL BRUT</td>
                  <td>—</td>
                  <td>{fmtN(selectedBulletin.brut)} F</td>
                </tr>
                
                {/* RETENUES */}
                <tr className="section-header">
                  <td colSpan="3">RETENUES SALARIALES</td>
                </tr>
                <tr>
                  <td>CNSS Salarial</td>
                  <td>3,6%</td>
                  <td>-{fmtN(selectedBulletin.cnss_salarie)} F</td>
                </tr>
                <tr>
                  <td>ITS (Impôt sur Traitements et Salaires)</td>
                  <td>—</td>
                  <td>-{fmtN(selectedBulletin.its)} F</td>
                </tr>
                {selectedBulletin.autres_retenues > 0 && (
                  <tr>
                    <td>Autres retenues</td>
                    <td>—</td>
                    <td>-{fmtN(selectedBulletin.autres_retenues)} F</td>
                  </tr>
                )}
                {selectedBulletin.avances > 0 && (
                  <tr>
                    <td>Avances sur salaire</td>
                    <td>—</td>
                    <td>-{fmtN(selectedBulletin.avances)} F</td>
                  </tr>
                )}
                {selectedBulletin.acomptes > 0 && (
                  <tr>
                    <td>Acomptes versés</td>
                    <td>—</td>
                    <td>-{fmtN(selectedBulletin.acomptes)} F</td>
                  </tr>
                )}
                {selectedBulletin.saisies_arrets > 0 && (
                  <tr>
                    <td>Oppositions (Saisies & Arrêts)</td>
                    <td>—</td>
                    <td>-{fmtN(selectedBulletin.saisies_arrets)} F</td>
                  </tr>
                )}
                <tr className="subtotal">
                  <td>TOTAL RETENUES</td>
                  <td>—</td>
                  <td>-{fmtN(totalRetenues)} F</td>
                </tr>
                
                {/* NETS */}
                <tr>
                  <td><strong>NET IMPOSABLE</strong></td>
                  <td>—</td>
                  <td><strong>{fmtN(selectedBulletin.net_imposable)} F</strong></td>
                </tr>
                <tr style={{background: '#fafafa'}}>
                  <td><strong style={{fontSize: '14px'}}>NET À PAYER</strong></td>
                  <td>—</td>
                  <td><strong style={{fontSize: '14px', color: '#C9A84C'}}>{fmtN(selectedBulletin.net_a_payer)} F</strong></td>
                </tr>
                
                
              </tbody>
            </table>
          </div>
          
          {/* NET À PAYER (Grande section) */}
          <div className="fp-net">
            <span className="fp-net-lbl">Net à payer</span>
            <span className="fp-net-val">{fmtN(selectedBulletin.net_a_payer)} F CFA</span>
          </div>
          
          {/* PIED DE PAGE */}
          <div className="fp-footer">
            <span>Émis le {new Date().toLocaleDateString('fr-FR')}</span>
            <span>CNSS Bénin • Ce bulletin doit être conservé sans limitation de durée</span>
          </div>
          
          {/* DÉTAILS OPPOSITIONS SI EXISTANTS */}
          {selectedBulletin.details_oppositions && (
            <div style={{padding: '15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6', fontSize: '11px'}}>
              <strong>Détails des oppositions :</strong><br/>
              {selectedBulletin.details_oppositions}
            </div>
          )}
          
          {/* SIGNATURES */}
          <div className="fp-sign">
            <div className="fp-sign-box">
              <div className="fp-sign-line"></div>
              <div className="fp-sign-lbl">Signature Employeur</div>
            </div>
            <div className="fp-sign-box">
              <div className="fp-sign-line"></div>
              <div className="fp-sign-lbl">Signature Employé(e)</div>
            </div>
          </div>
        </div>
        
        {/* Boutons de contrôle visibles */}
        <div className="print-controls no-print" style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: '#0a1628',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10
        }}>
          <button 
            onClick={() => { setViewMode('list'); setSelectedBulletin(null); }} 
            className="btn-back"
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Retour à la liste
          </button>
          <button 
            onClick={() => window.print()} 
            className="btn-print"
            style={{
              padding: '10px 20px',
              backgroundColor: '#C9A84C',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            🖨️ Imprimer
          </button>
          <button 
            onClick={() => downloadPdfBulletin(selectedBulletin)} 
            className="btn-print"
            style={{
              padding: '10px 20px',
              backgroundColor: '#42a5f5',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            📄 Télécharger PDF
          </button>
        </div>
      </div>
    );
  }

  if (viewMode === 'detail' && selectedBulletin) {
    return (
      <div className="paie-module">
        <div className="paie-header">
          <div>
            <button onClick={() => { setViewMode('list'); setSelectedBulletin(null); }} className="btn-back">
              ← Retour à la liste
            </button>
            <h2>Bulletin de Paie - {selectedBulletin.employee?.user?.name || 'N/A'}</h2>
            <p style={{ color: '#8b95a5', marginTop: '8px' }}>
              Mois: {selectedBulletin.mois} • Statut: {getStatutBadge(selectedBulletin.statut)}
            </p>
          </div>
        </div>

        <div className="bulletin-detail">
          <div className="detail-section">
            <h3>Informations Employé</h3>
            <table className="detail-table">
              <tbody>
                <tr><td>Nom</td><td>{selectedBulletin.employee?.user?.name}</td></tr>
                <tr><td>Email</td><td>{selectedBulletin.employee?.user?.email}</td></tr>
                <tr><td>Contrat</td><td>{selectedBulletin.employee?.contrat || 'N/A'}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="detail-section">
            <h3>Éléments de Rémunération</h3>
            <table className="detail-table">
              <tbody>
                <tr><td>Salaire de base</td><td>{fmtN(selectedBulletin.salaire_base)} F</td></tr>
                <tr><td>Primes</td><td>{fmtN(selectedBulletin.primes)} F</td></tr>
                <tr><td>Heures supplémentaires</td><td>{fmtN(selectedBulletin.heures_supplementaires)} F</td></tr>
                <tr><td>Avantages en nature</td><td>{fmtN(selectedBulletin.avantages_nature)} F</td></tr>
                <tr className="total-row"><td><strong>Salaire Brut</strong></td><td><strong>{fmtN(selectedBulletin.brut)} F</strong></td></tr>
              </tbody>
            </table>
          </div>

          <div className="detail-section">
            <h3>Retenues Salariales</h3>
            <table className="detail-table">
              <tbody>
                <tr><td>CNSS Salarié (3.6%)</td><td>{fmtN(selectedBulletin.cnss_salarie)} F</td></tr>
                <tr><td>ITS (Impôt sur Traitements et Salaires)</td><td>{fmtN(selectedBulletin.its)} F</td></tr>
                <tr><td>Autres retenues</td><td>{fmtN(selectedBulletin.autres_retenues)} F</td></tr>
                {selectedBulletin.avances > 0 && <tr><td>Avances sur salaire</td><td>{fmtN(selectedBulletin.avances)} F</td></tr>}
                {selectedBulletin.acomptes > 0 && <tr><td>Acomptes versés</td><td>{fmtN(selectedBulletin.acomptes)} F</td></tr>}
                {selectedBulletin.saisies_arrets > 0 && <tr><td>Oppositions (Saisies & Arrêts)</td><td>{fmtN(selectedBulletin.saisies_arrets)} F</td></tr>}
              </tbody>
            </table>
          </div>

          

          <div className="detail-section net-payer">
            <h3>Net à Payer</h3>
            <div className="net-amount">{fmtN(selectedBulletin.net_a_payer)} F CFA</div>
          </div>

          {selectedBulletin.notes && (
            <div className="detail-section">
              <h3>Notes</h3>
              <p>{selectedBulletin.notes}</p>
            </div>
          )}

          {selectedBulletin.details_oppositions && (
            <div className="detail-section">
              <h3>Détails des Oppositions</h3>
              <p>{selectedBulletin.details_oppositions}</p>
            </div>
          )}

          <div className="detail-actions">
            <button onClick={() => printBulletin(selectedBulletin)} className="btn-print-detail">
              🖨️ Imprimer la Fiche
            </button>
            {selectedBulletin.statut === 'brouillon' && (
              <>
                <button onClick={() => openModal(selectedBulletin)} className="btn-edit">Modifier</button>
                <button onClick={() => handleValider(selectedBulletin.id)} className="btn-validate">Valider</button>
                <button onClick={() => handleDelete(selectedBulletin.id)} className="btn-delete">Supprimer</button>
              </>
            )}
            {selectedBulletin.statut === 'valide' && (
              <button onClick={() => handlePayer(selectedBulletin.id)} className="btn-pay">Marquer comme Payé</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paie-module">
      <div className="paie-header">
        <div>
          <h2>Gestion de la Paie</h2>
          <p style={{ color: '#8b95a5', marginTop: '8px' }}>
            {bulletins.length} bulletin(s) pour le mois sélectionné
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleGenererMasse} className="btn-generate">
            ⚡ Générer en Masse
          </button>
          <button onClick={() => openModal()} className="btn-add">
            + Nouveau Bulletin
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="paie-filters">
        <div className="filter-group">
          <label>Mois <span style={{color: '#ef5350'}}>*</span></label>
          <input
            type="month"
            value={filters.mois}
            onChange={(e) => setFilters({ ...filters, mois: e.target.value })}
            style={{fontWeight: '600'}}
          />
        </div>
        <div className="filter-group">
          <label>Statut</label>
          <select value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })}>
            <option value="">Tous les statuts</option>
            <option value="brouillon">Brouillon</option>
            <option value="valide">Validé</option>
            <option value="paye">Payé</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Employé (optionnel)</label>
          <select value={filters.employee_id} onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}>
            <option value="">Tous les employés</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.user?.name || 'N/A'}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste des bulletins */}
      {loading ? (
        <div className="loading">Chargement...</div>
      ) : bulletins.length === 0 ? (
        <div className="empty-state">
          <p>Aucun bulletin de paie pour ce mois</p>
          <button onClick={() => openModal()} className="btn-add">Créer le premier bulletin</button>
        </div>
      ) : (
        <div className="bulletins-table">
          <table>
            <thead>
              <tr>
                <th>Employé</th>
                <th>Mois</th>
                <th>Salaire Brut</th>
                <th>Retenues</th>
                <th>Net à Payer</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bulletins.map((bulletin, idx) => (
                <tr key={bulletin.id} style={{ backgroundColor: idx % 2 === 0 ? '#0a1628' : '#060d18' }}>
                  <td>{bulletin.employee?.user?.name || 'N/A'}</td>
                  <td>{bulletin.mois}</td>
                  <td>{fmtN(bulletin.brut)} F</td>
                  <td>{fmtN(parseInt(bulletin.cnss_salarie) + parseInt(bulletin.its) + parseInt(bulletin.autres_retenues) + parseInt(bulletin.avances || 0) + parseInt(bulletin.acomptes || 0) + parseInt(bulletin.saisies_arrets || 0))} F</td>
                  <td><strong>{fmtN(bulletin.net_a_payer)} F</strong></td>
                  <td>{getStatutBadge(bulletin.statut)}</td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => viewDetails(bulletin)} className="btn-view" title="Voir la fiche">👁️</button>
                      <button onClick={() => printBulletin(bulletin)} className="btn-print-small" title="Imprimer">🖨️</button>
                      <button onClick={() => downloadPdfBulletin(bulletin)} className="btn-print-small" title="Télécharger PDF">📄</button>
                      {bulletin.statut === 'brouillon' && (
                        <>
                          <button onClick={() => openModal(bulletin)} className="btn-edit-small" title="Modifier">✏️</button>
                          <button onClick={() => handleValider(bulletin.id)} className="btn-validate-small" title="Valider">✓</button>
                          <button onClick={() => handleDelete(bulletin.id)} className="btn-delete-small" title="Supprimer">🗑️</button>
                        </>
                      )}
                      {bulletin.statut === 'valide' && (
                        <button onClick={() => handlePayer(bulletin.id)} className="btn-pay-small" title="Marquer payé">💰</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedBulletin ? 'Modifier le Bulletin' : 'Nouveau Bulletin de Paie'}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Employé *</label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                    disabled={!!selectedBulletin}
                  >
                    <option value="">Sélectionner un employé</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.user?.name || 'N/A'}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Mois *</label>
                  <input
                    type="month"
                    value={formData.mois}
                    onChange={(e) => setFormData({ ...formData, mois: e.target.value })}
                    required
                    disabled={!!selectedBulletin}
                  />
                </div>

                <div className="form-group">
                  <label>Salaire de Base *</label>
                  <input
                    type="number"
                    value={formData.salaire_base}
                    onChange={(e) => setFormData({ ...formData, salaire_base: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Primes</label>
                  <input
                    type="number"
                    value={formData.primes}
                    onChange={(e) => setFormData({ ...formData, primes: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Heures Supplémentaires</label>
                  <input
                    type="number"
                    value={formData.heures_supplementaires}
                    onChange={(e) => setFormData({ ...formData, heures_supplementaires: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Avantages en Nature</label>
                  <input
                    type="number"
                    value={formData.avantages_nature}
                    onChange={(e) => setFormData({ ...formData, avantages_nature: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Autres Retenues</label>
                  <input
                    type="number"
                    value={formData.autres_retenues}
                    onChange={(e) => setFormData({ ...formData, autres_retenues: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Avances sur Salaire</label>
                  <input
                    type="number"
                    value={formData.avances}
                    onChange={(e) => setFormData({ ...formData, avances: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Acomptes Versés</label>
                  <input
                    type="number"
                    value={formData.acomptes}
                    onChange={(e) => setFormData({ ...formData, acomptes: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Oppositions (Saisies & Arrêts)</label>
                  <input
                    type="number"
                    value={formData.saisies_arrets}
                    onChange={(e) => setFormData({ ...formData, saisies_arrets: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Détails des Oppositions</label>
                  <textarea
                    value={formData.details_oppositions}
                    onChange={(e) => setFormData({ ...formData, details_oppositions: e.target.value })}
                    placeholder="Détails sur les oppositions, saisies ou arrêts sur salaire..."
                    rows={2}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes ou commentaires..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Aperçu des calculs */}
              {calculs && (
                <div className="calculs-preview">
                  <h4>Aperçu des Calculs</h4>
                  <div className="calculs-grid">
                    <div><span>Brut:</span><strong>{fmtN(calculs.brut)} F</strong></div>
                    <div><span>CNSS Salarié:</span><strong>{fmtN(calculs.cnss_salarie)} F</strong></div>
                    <div><span>ITS:</span><strong>{fmtN(calculs.its)} F</strong></div>
                    <div><span>Net à Payer:</span><strong className="net-highlight">{fmtN(calculs.net_a_payer)} F</strong></div>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  {selectedBulletin ? 'Mettre à Jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaieModuleFonctionnel;
